import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, BarChart3, TrendingUp, TrendingDown, Activity, DollarSign, ArrowUpRight, ArrowDownRight, Briefcase, Coins, Package } from "lucide-react";

// Mock dataset — mirrors the warerastats.io structure so the live
// integration drops in cleanly once the game opens its beta API.

const MOCK_TRADES = [
  { slug: "steel",     item: "Steel",      qty: 25,  unit: 1.61,  when: "—" },
  { slug: "steak",     item: "Steak",      qty: 1,   unit: 3.60,  when: "—" },
  { slug: "limestone", item: "Limestone",  qty: 486, unit: 0.082, when: "—" },
  { slug: "ammo",      item: "Ammo",       qty: 64,  unit: 0.41,  when: "—" },
  { slug: "grain",     item: "Grain",      qty: 220, unit: 0.18,  when: "—" },
];

const MOCK_TOP_NATIONS = [
  { name: "United States of America", iso: "us", wealth: 245641, citizens: 12840 },
  { name: "China",                    iso: "cn", wealth: 198420, citizens: 11205 },
  { name: "Germany",                  iso: "de", wealth:  82150, citizens:  5670 },
  { name: "Japan",                    iso: "jp", wealth:  69330, citizens:  4320 },
  { name: "United Kingdom",           iso: "gb", wealth:  47580, citizens:  3180 },
];

// KPI + wage + inflation data keyed by selected time range.
// Numbers scale realistically: 24h tight band → All shows wider history.
const STATS_BY_RANGE = {
  "24H": {
    avgWage:     "0.133",
    totalWages:  "84,520",
    tradeVolume: "12,840",
    avgWageDelta: 1.2,
    totalWagesDelta: -0.8,
    tradeVolumeDelta: 4.5,
    wage: { min: "0.106", avg: "0.133", max: "0.159" },
    inflation: { yesterday: 0.34, week: null, month: null },
  },
  "7D": {
    avgWage:     "0.128",
    totalWages:  "578,140",
    tradeVolume: "89,205",
    avgWageDelta: 0.6,
    totalWagesDelta: 2.1,
    tradeVolumeDelta: 7.8,
    wage: { min: "0.095", avg: "0.128", max: "0.182" },
    inflation: { yesterday: 0.34, week: 1.82, month: null },
  },
  "30D": {
    avgWage:     "0.121",
    totalWages:  "2,401,830",
    tradeVolume: "384,250",
    avgWageDelta: -1.4,
    totalWagesDelta: 5.4,
    tradeVolumeDelta: 11.2,
    wage: { min: "0.082", avg: "0.121", max: "0.205" },
    inflation: { yesterday: 0.34, week: 1.82, month: 6.41 },
  },
  "ALL": {
    avgWage:     "0.117",
    totalWages:  "8,920,610",
    tradeVolume: "1,205,840",
    avgWageDelta: null,
    totalWagesDelta: null,
    tradeVolumeDelta: null,
    wage: { min: "0.054", avg: "0.117", max: "0.298" },
    inflation: { yesterday: 0.34, week: 1.82, month: 6.41 },
  },
};

const TIME_RANGES = ["24H", "7D", "30D", "ALL"];

export default function Stats() {
  const [range, setRange] = useState("24H");
  const data = useMemo(() => STATS_BY_RANGE[range], [range]);

  useEffect(() => {
    document.title = "Stats · Territoria";
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="stats-page">
      {/* Header */}
      <div className="mb-7 md:mb-9 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to="/wiki" className="font-mono text-[10px] tracking-[0.25em] text-[#22d3ee] uppercase hover:text-[#67e8f9] inline-flex items-center gap-1" data-testid="stats-back-link">
            <ChevronLeft className="w-3 h-3" /> back to codex
          </Link>
          <div className="flex items-center gap-3 mt-3">
            <BarChart3 className="w-8 h-8 text-[#22d3ee]" />
            <h1 className="font-display text-3xl md:text-5xl text-[var(--t-text)] uppercase leading-tight">Territoria Stats</h1>
          </div>
          <p className="text-sm md:text-base text-[var(--t-text-dim)] mt-3 max-w-2xl">
            Live game analytics — wages, trade flow, inflation, the meta. Pulled directly from the engine the moment open beta goes live.
          </p>
        </div>

        {/* Range selector — placeholder UI, wires to real API on beta */}
        <div className="flex items-center gap-1 border border-[var(--t-border)] p-0.5" data-testid="stats-range">
          {TIME_RANGES.map(r => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] uppercase transition ${
                range === r
                  ? "bg-[rgba(34,211,238,0.12)] text-[#67e8f9]"
                  : "text-[var(--t-text-muted)] hover:text-[var(--t-cyan)]"
              }`}
              data-testid={`stats-range-${r.toLowerCase()}`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Beta banner */}
      <div className="panel panel-glow border-[#22d3ee]/40 p-4 md:p-5 mb-7 md:mb-8 flex items-start gap-3 bg-[rgba(34,211,238,0.04)]" data-testid="stats-beta-banner">
        <Activity className="w-5 h-5 text-[#22d3ee] mt-0.5 flex-shrink-0 pulse-glow" />
        <div className="min-w-0">
          <div className="font-mono text-[10px] md:text-xs tracking-[0.25em] text-[#22d3ee] uppercase">// Awaiting Live Feed</div>
          <p className="text-sm text-[var(--t-text)] mt-1">
            The Territoria engine connects to this dashboard at <strong className="text-[#67e8f9]">open beta launch</strong>. Numbers below are placeholders showing the layout you&apos;ll get day one.
          </p>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-7 md:mb-8" data-testid="stats-kpis">
        <Kpi
          icon={Briefcase}
          label={`Average Wage (${range})`}
          value={data.avgWage}
          delta={data.avgWageDelta}
          accent="#22d3ee"
          testid="kpi-avg-wage"
        />
        <Kpi
          icon={DollarSign}
          label={`Total Wages (${range})`}
          value={data.totalWages}
          delta={data.totalWagesDelta}
          accent="#22d3ee"
          testid="kpi-total-wages"
        />
        <Kpi
          icon={Coins}
          label={`Trade Volume (${range})`}
          value={data.tradeVolume}
          delta={data.tradeVolumeDelta}
          accent="#22d3ee"
          testid="kpi-trade-volume"
        />
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-4 md:gap-5 mb-7 md:mb-8">
        {/* Recent Trades */}
        <section className="panel panel-glow corner-frame p-5 md:p-6" data-testid="stats-trades">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] text-[#22d3ee] uppercase">// Recent Trades</div>
              <h2 className="font-display text-xl md:text-2xl text-[var(--t-text)] uppercase mt-1">Latest Market Activity</h2>
            </div>
            <Activity className="w-5 h-5 text-[#22d3ee] mt-1" />
          </div>
          <div className="space-y-1.5" data-testid="stats-trades-list">
            {MOCK_TRADES.map((t, i) => (
              <Link
                key={t.slug}
                to="/wiki/items"
                className="flex items-center gap-3 py-2.5 px-3 border border-transparent hover:border-[var(--t-border)] hover:bg-[rgba(34,211,238,0.04)] transition"
                data-testid={`stats-trade-${i}`}
                title="Open Items vault"
              >
                <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center bg-[rgba(34,211,238,0.08)] border border-[var(--t-border)]">
                  <Package className="w-4 h-4 text-[#22d3ee]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-sm text-[var(--t-text)] uppercase tracking-[0.05em] truncate">{t.item}</div>
                  <div className="font-mono text-[10px] tracking-[0.15em] text-[var(--t-text-muted)] uppercase">qty {t.qty} · {t.when}</div>
                </div>
                <div className="font-mono text-sm text-[#67e8f9] tabular-nums flex-shrink-0">
                  {t.unit.toFixed(3)}<span className="text-[var(--t-text-muted)] text-xs ml-1">×{t.qty}</span>
                </div>
              </Link>
            ))}
          </div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-[var(--t-text-muted)] uppercase mt-4 text-center">
            // sample data — live feed at beta launch
          </div>
        </section>

        {/* Inflation + Wage Market stack */}
        <div className="space-y-4 md:space-y-5">
          <section className="panel panel-glow corner-frame p-5 md:p-6" data-testid="stats-inflation">
            <div className="font-mono text-[10px] tracking-[0.25em] text-[#22d3ee] uppercase">// Inflation</div>
            <h2 className="font-display text-lg md:text-xl text-[var(--t-text)] uppercase mt-1 mb-4">Daily Price Level Change</h2>
            <div className="space-y-2.5">
              <InflationRow label="Yesterday" delta={data.inflation.yesterday} />
              <InflationRow label="Past 7 days" delta={data.inflation.week} />
              <InflationRow label="Past 30 days" delta={data.inflation.month} />
            </div>
          </section>

          <section className="panel panel-glow corner-frame p-5 md:p-6" data-testid="stats-wages">
            <div className="font-mono text-[10px] tracking-[0.25em] text-[#22d3ee] uppercase">// Wage Market</div>
            <h2 className="font-display text-lg md:text-xl text-[var(--t-text)] uppercase mt-1 mb-4">Current Labour Rates</h2>
            <div className="grid grid-cols-3 gap-2 text-center">
              <WageCell label="Min" value={data.wage.min} />
              <WageCell label="Avg" value={data.wage.avg} accent />
              <WageCell label="Max" value={data.wage.max} />
            </div>
          </section>
        </div>
      </div>

      {/* Top Nations Leaderboard */}
      <section className="panel panel-glow corner-frame p-5 md:p-6" data-testid="stats-nations">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] text-[#22d3ee] uppercase">// Top Nations</div>
            <h2 className="font-display text-xl md:text-2xl text-[var(--t-text)] uppercase mt-1">Nation Wealth Leaderboard</h2>
          </div>
          <span className="font-mono text-[10px] tracking-[0.2em] text-[var(--t-text-muted)] uppercase">{range} window</span>
        </div>
        <div className="overflow-x-auto -mx-2 md:mx-0">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--t-border)]">
                <th className="text-left font-mono text-[10px] tracking-[0.25em] text-[var(--t-text-muted)] uppercase py-2 px-3">#</th>
                <th className="text-left font-mono text-[10px] tracking-[0.25em] text-[var(--t-text-muted)] uppercase py-2 px-3">Nation</th>
                <th className="text-right font-mono text-[10px] tracking-[0.25em] text-[var(--t-text-muted)] uppercase py-2 px-3">Wealth</th>
                <th className="text-right font-mono text-[10px] tracking-[0.25em] text-[var(--t-text-muted)] uppercase py-2 px-3">Citizens</th>
              </tr>
            </thead>
            <tbody>
              {MOCK_TOP_NATIONS.map((n, i) => (
                <tr key={n.iso} className="border-b border-[var(--t-border)]/40 hover:bg-[rgba(34,211,238,0.04)] transition" data-testid={`stats-nation-${i}`}>
                  <td className="py-2.5 px-3 font-mono text-xs text-[var(--t-text-muted)]">{String(i + 1).padStart(2, "0")}</td>
                  <td className="py-2.5 px-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={`https://flagcdn.com/h24/${n.iso}.png`}
                        alt=""
                        className="w-7 h-5 object-cover border border-[var(--t-border)] flex-shrink-0"
                        onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                        data-testid={`stats-nation-${i}-flag`}
                      />
                      <div className="font-display text-sm text-[var(--t-text)] uppercase tracking-[0.05em]">{n.name}</div>
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono text-sm text-[#67e8f9] tabular-nums">${n.wealth.toLocaleString()}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-sm text-[var(--t-text-dim)] tabular-nums">{n.citizens.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="font-mono text-[10px] tracking-[0.2em] text-[var(--t-text-muted)] uppercase mt-4 text-center">
          // sample rankings — live data syncs at open beta
        </div>
      </section>

      {/* Footer note */}
      <p className="font-mono text-[10px] md:text-xs tracking-[0.2em] text-[var(--t-text-muted)] uppercase text-center mt-8">
        // Built into Territoria.wiki ·
        <span className="text-[#22d3ee]"> Beta sync coming soon</span>
      </p>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, delta, accent, testid }) {
  return (
    <div className="panel panel-glow p-5 md:p-6" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-text-muted)] uppercase">{label}</div>
          <div className="font-display text-3xl md:text-4xl tabular-nums mt-2" style={{ color: accent }} data-testid={`${testid}-value`}>{value}</div>
        </div>
        <div className="w-10 h-10 border bg-[rgba(34,211,238,0.08)] flex items-center justify-center flex-shrink-0" style={{ borderColor: accent }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
      </div>
      {delta !== null && delta !== undefined ? (
        <div className={`mt-3 inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.2em] uppercase ${delta >= 0 ? "text-[#43b581]" : "text-[#f04747]"}`}>
          {delta >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {delta >= 0 ? "+" : ""}{delta}%
        </div>
      ) : (
        <div className="mt-3 font-mono text-[10px] tracking-[0.18em] text-[var(--t-text-muted)] uppercase">awaiting live feed</div>
      )}
    </div>
  );
}

function InflationRow({ label, delta }) {
  const hasData = typeof delta === "number";
  const positive = hasData && delta >= 0;
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5 border border-[var(--t-border)] bg-[rgba(5,12,28,0.4)]">
      <span className="font-mono text-[11px] tracking-[0.18em] text-[var(--t-text-dim)] uppercase">{label}</span>
      <span className={`inline-flex items-center gap-1 font-mono text-sm tabular-nums ${hasData ? (positive ? "text-[#43b581]" : "text-[#f04747]") : "text-[var(--t-text-muted)]"}`}>
        {hasData ? (
          <>
            {positive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
            {positive ? "+" : ""}{delta.toFixed(2)}%
          </>
        ) : "—"}
      </span>
    </div>
  );
}

function WageCell({ label, value, accent }) {
  return (
    <div className={`p-3 border ${accent ? "border-[#22d3ee]/40 bg-[rgba(34,211,238,0.06)]" : "border-[var(--t-border)]"}`}>
      <div className="font-mono text-[10px] tracking-[0.22em] text-[var(--t-text-muted)] uppercase">{label}</div>
      <div className={`font-display text-xl md:text-2xl tabular-nums mt-1 ${accent ? "text-[#67e8f9]" : "text-[var(--t-text)]"}`}>{value}</div>
    </div>
  );
}
